// ***************************************************************************************
// The game's random sources, ported from lib/maths.cpp.
//
// Two separate generators, and which one is used where is not a detail:
//
//   PCG32       - Maths_GameRand. Builds the board. A different value anywhere in this
//                 sequence is a different puzzle.
//   MT19937     - game/mt19937ar.c. Deals letters for the modes whose boards are dealt
//                 rather than constructed (Daily 9x12, Burst 6x6).
//
// Daily Score Attack is a CONSTRUCTED mode and uses PCG32 only. MT19937 is here so the
// dealt modes stay reachable without a second porting exercise later.
//
// Nothing cosmetic may draw from either of these. Rendering must never be able to change
// what the board is.
// ***************************************************************************************

const PCG_MASK = 0xffffffffffffffffn;
const PCG_MULT = 6364136223846793005n;

/** Maths_SeedGameRand's stream selector. */
export const GAME_RNG_STREAM = 0xda3e39cb94b95bdbn;

export interface Rng {
  /** A uniform value in [0, range). `range === 0` gives 0. */
  bounded(range: number): number;
}

// ***************************************************************************************
// PCG32 (O'Neill, 2014), matching Maths_RngNext / Maths_RngSeed / Maths_RngBounded.
// ***************************************************************************************
export class Pcg32 implements Rng {
  private state: bigint;
  private readonly inc: bigint;

  constructor(seed: number, stream: bigint) {
    this.state = 0n;
    this.inc = ((stream << 1n) | 1n) & PCG_MASK;

    this.next();
    this.state = (this.state + (BigInt(seed >>> 0) & PCG_MASK)) & PCG_MASK;
    this.next();
  }

  next(): number {
    const old = this.state;

    this.state = ((old * PCG_MULT) + this.inc) & PCG_MASK;

    const xorshifted = Number((((old >> 18n) ^ old) >> 27n) & 0xffffffffn) >>> 0;
    const rot = Number((old >> 59n) & 0x1fn);

    // The C rotate is `(x >> rot) | (x << ((0u - rot) & 31u))` on a U32. At rot 0 the left
    // shift is by 32, which C leaves undefined and x86 executes as a shift by 0, giving
    // x | x = x. JavaScript's `<< 32` is also a shift by 0, so the two agree.
    return ((xorshifted >>> rot) | (xorshifted << ((32 - rot) & 31))) >>> 0;
  }

  // Rejection sampling rather than a modulo, and the rejection is load-bearing: a
  // rejected draw still CONSUMES a value. Substituting `% range` would produce
  // perfectly good random numbers that step out of phase with the C after the first
  // rejection, and therefore a different board.
  bounded(range: number): number {
    if (range === 0) return 0;

    const threshold = (4294967296 - range) % range;

    for (;;) {
      const value = this.next();

      if (value >= threshold) return value % range;
    }
  }
}

// ***************************************************************************************
// MT19937, a verbatim port of game/mt19937ar.c (Matsumoto and Nishimura's reference
// implementation, unmodified).
//
// `>>> 0` throughout because JavaScript's bitwise operators produce SIGNED 32-bit results
// and every value here is unsigned.
// ***************************************************************************************
const MT_N = 624;
const MT_M = 397;
const MT_UPPER = 0x80000000;
const MT_LOWER = 0x7fffffff;
const MT_MATRIX_A = 0x9908b0df;

export class MersenneTwister implements Rng {
  private readonly mt = new Uint32Array(MT_N);
  private mti = MT_N + 1;

  constructor(seed: number) {
    this.mt[0] = seed >>> 0;

    for (this.mti = 1; this.mti < MT_N; this.mti++) {
      const prev = this.mt[this.mti - 1]! ^ (this.mt[this.mti - 1]! >>> 30);

      this.mt[this.mti] = (Math.imul(1812433253, prev) + this.mti) >>> 0;
    }
  }

  int32(): number {
    let y: number;

    if (this.mti >= MT_N) {
      let kk: number;

      for (kk = 0; kk < MT_N - MT_M; kk++) {
        y = ((this.mt[kk]! & MT_UPPER) | (this.mt[kk + 1]! & MT_LOWER)) >>> 0;
        this.mt[kk] = (this.mt[kk + MT_M]! ^ (y >>> 1) ^ (y & 1 ? MT_MATRIX_A : 0)) >>> 0;
      }

      for (; kk < MT_N - 1; kk++) {
        y = ((this.mt[kk]! & MT_UPPER) | (this.mt[kk + 1]! & MT_LOWER)) >>> 0;
        this.mt[kk] = (this.mt[kk + (MT_M - MT_N)]! ^ (y >>> 1) ^ (y & 1 ? MT_MATRIX_A : 0)) >>> 0;
      }

      y = ((this.mt[MT_N - 1]! & MT_UPPER) | (this.mt[0]! & MT_LOWER)) >>> 0;
      this.mt[MT_N - 1] = (this.mt[MT_M - 1]! ^ (y >>> 1) ^ (y & 1 ? MT_MATRIX_A : 0)) >>> 0;

      this.mti = 0;
    }

    y = this.mt[this.mti++]!;

    y ^= y >>> 11;
    y = (y ^ ((y << 7) & 0x9d2c5680)) >>> 0;
    y = (y ^ ((y << 15) & 0xefc60000)) >>> 0;
    y ^= y >>> 18;

    return y >>> 0;
  }

  /** genrand_real2: [0,1) with 32-bit resolution. */
  real2(): number {
    return this.int32() * (1.0 / 4294967296.0);
  }

  bounded(range: number): number {
    if (range === 0) return 0;

    return this.int32() % range;
  }
}
